import '../styles/pages/patrol.css';
import { Modal } from '@mantine/core';
import Button from '../components/Button';
import InfoDisplay from '../components/InfoDisplay';
import React, { useEffect, useState, useRef } from 'react';
import Alert from '../components/Alert';
import { apiRequest } from '../utils/api';
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const PatrolDetail = () => {
  const location = useLocation();
  const ID_HIS = location.state?.ID_HIS;

  const [isSMSModalOpen, setIsSMSModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [alertOpen, setAlertOpen] = useState({ isOpen: false, message: '' });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [recordDetail, setRecordDetail] = useState(null);
  const reportRef = useRef();

  useEffect(() => {
    if (ID_HIS) {
      handleViewRecord(ID_HIS);
    }
  }, [ID_HIS]);

  const showAlert = message => {
    setAlertOpen({ isOpen: true, message });
  };
  const closeAlert = () => {
    setAlertOpen({ isOpen: false, message: '' });
  };

  const handleSMSModalOpen = () => {
    setIsSMSModalOpen(true);
  };
  const handleSMSModalClose = () => {
    setIsSMSModalOpen(false);
  };

  const handleSMSSend = async () => {
    if (!phoneNumber) {
      showAlert('전화번호가 없습니다.');
      return;
    }

    try {
      console.log('handleSMSSend start');
      setIsSending(true);

      const res = await apiRequest('/sendReportMMS', 'POST', {
        to: phoneNumber,
        record: recordDetail,
      });

      if (res.success) {
        showAlert('문자가 성공적으로 전송되었습니다.');
        handleSMSModalClose();
        setPhoneNumber('');
      } else {
        showAlert('문자 전송에 실패했습니다.');
      }
    } catch (err) {
      console.error('SMS 전송 중 오류:', err);
      showAlert('문자 전송 중 오류가 발생했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  const handlePhoneChange = e => {
    let value = e.target.value.replace(/[^0-9]/g, '');

    if (value.length <= 3) {
      setPhoneNumber(value);
    } else if (value.length <= 7) {
      setPhoneNumber(value.slice(0, 3) + '-' + value.slice(3));
    } else {
      setPhoneNumber(value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11));
    }
  };

  const handleViewRecord = async ID_HIS => {
    try {
      const res = await apiRequest('/getRecordDetail', 'POST', { ID: ID_HIS });
      setPhoneNumber(res.data.memoRow.STORE_PHONE);
      setRecordDetail(res.data);
    } catch (error) {
      console.error('상세 조회 실패:', error);
      showAlert('상세 정보를 불러오는 데 실패했습니다.');
    }
  };

  const handleSaveImage = async () => {
    if (!recordDetail) return;

    const element = reportRef.current;
    if (!element) {
      showAlert('출력 대상이 없습니다.');
      return;
    }

    try {
      // 잠깐 DOM을 보이게 만듬 (혹은 offscreen 위치에 있어야 함)
      element.style.display = 'block';

      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: true,
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let adjustedWidth = imgWidth;
      let adjustedHeight = imgHeight;

      if (imgHeight > pageHeight) {
        const ratio = pageHeight / imgHeight;
        adjustedWidth = imgWidth * ratio;
        adjustedHeight = imgHeight * ratio;
      }

      const x = (pageWidth - adjustedWidth) / 2;
      const y = (pageHeight - adjustedHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, adjustedWidth, adjustedHeight);
      pdf.save(`순찰기록_${recordDetail?.memoRow?.REG_DTM || 'report'}.pdf`);

      element.style.display = 'none'; // 다시 숨김
    } catch (err) {
      console.error('PDF 저장 오류:', err);
      showAlert('PDF 저장 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="mobile-container">
      <div className="record-modal-container">
        <div className="modal-section">
          <div className="modal-background-wrapper">
            <span className="modal-big-title">DAEDEOK PATROL REPORT</span>
            <div className="info-display">
              <InfoDisplay label="순찰시간" value={recordDetail?.memoRow?.REG_DTM} />
              <InfoDisplay label="태크명" value={recordDetail?.memoRow?.TAGNUM} />
              <InfoDisplay label="사업장명" value={recordDetail?.memoRow?.STORE_NAME} />
              <InfoDisplay label="위치장소" value={recordDetail?.memoRow?.STORE_ADDR} />
            </div>
          </div>

          <div className="modal-background-wrapper">
            <div className="title-logo-wrapper">
              <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo" />
              <p className="report-modal-title gmarket-font">점검결과 및 내용</p>
            </div>
            <div className="checklist-items-wrapper">
              {recordDetail?.checklistData?.length > 0 ? (
                recordDetail.checklistData.map((item, index) => (
                  <div key={index} className="checklist-item">
                    <div className="checklist-header">
                      <div className={`custom-checkbox ${item.checked ? 'checked' : ''}`}>
                        {item.checked && <span className="checkmark"></span>}
                      </div>
                      <span className="checklist-content">{item.content}</span>
                    </div>
                    {item.reason && (
                        <div className="checklist-reason">
                          <strong>사유: </strong>
                        {item.reason}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-data gmarket-font">점검사항이 없습니다.</div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-background-wrapper">
          <div className="title-logo-wrapper">
            <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo" />
            <p className="report-modal-title gmarket-font">전달사항 및 의견</p>
          </div>
          <div className="special-notes">{recordDetail?.memoRow ? recordDetail.memoRow.MEMO : <div className="no-images gmarket-font">특이사항이 없습니다.</div>}</div>
        </div>

        <div className="modal-background-wrapper">
          <div className="title-logo-wrapper">
            <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo" />
            <p className="report-modal-title gmarket-font">점검 증빙사진 (1장)</p>
          </div>
          <div className="attached-images">
            {recordDetail?.attachedImages?.length > 0 ? (
              <>
                {recordDetail.attachedImages.map((image, index) => (
                  <div key={index} className="image-item">
                    <img
                      src={`/public${image.url || image}`}
                      alt={`첨부사진 ${index + 1}`}
                      className="attached-image"
                      onClick={() => window.open(`/public${image.url || image}`, '_blank')}
                    />
                  </div>
                ))}
                <img src="/assets/images/dd-korean-logo.png" alt="로고" className="report-korean-logo" />
              </>
            ) : (
              <div className="no-images gmarket-font">첨부된 사진이 없습니다.</div>
            )}
          </div>
        </div>
      </div>

      <div className="button-wrapper">
        <Button className="bottom-button" onClick={handleSaveImage}>
          휴대폰 저장
        </Button>
        <Button className="bottom-button" onClick={handleSMSModalOpen}>
          SMS 전달하기
        </Button>
      </div>
      <div className="margin-bottom-section" />

      <Modal opened={isSMSModalOpen} onClose={handleSMSModalClose} zIndex={9999} centered size="md">
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
          <input
            type="text"
            placeholder="전화번호 입력"
            className="gray-input gmarket-font"
            maxLength={13}
            style={{ width: '100%' }}
            value={phoneNumber}
            onChange={handlePhoneChange}
          />
          <Button onClick={handleSMSSend} disabled={isSending} style={isSending ? { backgroundColor: '#ccc', cursor: 'not-allowed' } : {}} className="send-button">
            {isSending ? '전송중...' : '전송'}
          </Button>
        </div>
      </Modal>

      <Alert isOpen={alertOpen.isOpen} message={alertOpen.message} onConfirm={closeAlert} />

      <div ref={reportRef} id="report-print">
        <div className="record-modal-container">
          <div className="modal-section">
            <div className="modal-background-wrapper">
              <span className="modal-big-title">DAEDEOK PATROL REPORT</span>
              <div className="info-display">
                <InfoDisplay label="순찰시간" value={recordDetail?.memoRow?.REG_DTM} />
                <InfoDisplay label="태크명" value={recordDetail?.memoRow?.TAGNUM} />
                <InfoDisplay label="사업장명" value={recordDetail?.memoRow?.STORE_NAME} />
                <InfoDisplay label="위치장소" value={recordDetail?.memoRow?.STORE_ADDR} />
              </div>
            </div>
            <p className="modal-title gmarket-font">점검사항</p>
            <div className="checklist-items-wrapper">
              {recordDetail?.checklistData?.length > 0 ? (
                  recordDetail.checklistData.map((item, index) => (
                      <div key={index} className="checklist-item">
                        <div className="checklist-header">
                          <div className={`custom-checkbox ${item.checked ? 'checked' : ''}`}>
                            {item.checked && <span className="checkmark"></span>}
                          </div>
                          <span className="checklist-content">{item.content}</span>
                        </div>
                        {item.reason && (
                            <div className="checklist-reason">
                              <strong>사유: </strong>
                              {item.reason}
                            </div>
                        )}
                      </div>
                  ))
              ) : (
                  <div className="no-data gmarket-font">점검사항이 없습니다.</div>
              )}
            </div>
          </div>

          <div className="modal-background-wrapper">
            <div className="title-logo-wrapper">
              <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo"/>
              <p className="report-modal-title gmarket-font">전달사항 및 의견</p>
            </div>
            <div className="special-notes">{recordDetail?.memoRow ? recordDetail.memoRow.MEMO :
                <div className="no-images gmarket-font">특이사항이 없습니다.</div>}</div>
          </div>

          <div className="modal-background-wrapper">
            <div className="title-logo-wrapper">
              <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo"/>
              <p className="report-modal-title gmarket-font">점검 증빙사진 (1장)</p>
            </div>
            <div className="attached-images">
              {recordDetail?.attachedImages?.length > 0 ? (
                <>
                  {recordDetail.attachedImages.map((image, index) => (
                    <div key={index} className="image-item">
                      <img
                        src={`/public${image.url || image}`}
                        alt={`첨부사진 ${index + 1}`}
                        className="attached-image"
                        onClick={() => window.open(`/public${image.url || image}`, '_blank')}
                      />
                    </div>
                  ))}
                  <img src="/assets/images/dd-korean-logo.png" alt="로고" className="report-korean-logo" />
                </>
              ) : (
                <div className="no-images gmarket-font">첨부된 사진이 없습니다.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatrolDetail;
